import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit'
import { AuditService } from 'services/crm'

// Newest first — same convention as notes/activities feeds.
const auditAdapter = createEntityAdapter({
  sortComparer: (a, b) => (a.createdAt < b.createdAt ? 1 : -1),
})

export const fetchAuditByDeal = createAsyncThunk(
  'crm/audit/fetchByDeal',
  async (dealId, { rejectWithValue }) => {
    try {
      const res = await AuditService.getByDeal(dealId)
      return res.data
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || 'Error fetching audit log',
      )
    }
  },
)

export const fetchAuditByLead = createAsyncThunk(
  'crm/audit/fetchByLead',
  async (leadId, { rejectWithValue }) => {
    try {
      const res = await AuditService.getByLead(leadId)
      return res.data
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || 'Error fetching audit log',
      )
    }
  },
)

const initialState = auditAdapter.getInitialState({ loading: 'idle' })

const auditSlice = createSlice({
  name: 'crmAudit',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const onFulfilled = (state, action) => {
      state.loading = 'idle'
      // The history of a deal/lead can grow over time and we re-fetch on every
      // detail page mount. upsertMany keeps prior entries cached too, which
      // makes navigating between entities snappy.
      auditAdapter.upsertMany(state, action.payload)
    }
    const onPending = (state) => {
      state.loading = 'pending'
    }
    const onRejected = (state) => {
      state.loading = 'idle'
    }
    builder
      .addCase(fetchAuditByDeal.pending, onPending)
      .addCase(fetchAuditByDeal.fulfilled, onFulfilled)
      .addCase(fetchAuditByDeal.rejected, onRejected)
      .addCase(fetchAuditByLead.pending, onPending)
      .addCase(fetchAuditByLead.fulfilled, onFulfilled)
      .addCase(fetchAuditByLead.rejected, onRejected)
  },
})

const selectors = auditAdapter.getSelectors((state) => state.crmAudit)
export const selectAllAudit = selectors.selectAll

export const selectAuditByDeal = (dealId) =>
  createSelector([selectAllAudit], (entries) =>
    entries
      .filter((e) => Number(e.dealId) === Number(dealId))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
  )

export const selectAuditByLead = (leadId) =>
  createSelector([selectAllAudit], (entries) =>
    entries
      .filter((e) => Number(e.leadId) === Number(leadId))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
  )

export default auditSlice.reducer
